import React from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { testimonials, beforeAfter } from '../data/mock';

const Results = () => {
  return (
    <section id="results" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Testimonials Section */}
        <div className="mb-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 bg-teal-50 text-teal-700 font-semibold text-sm rounded-full mb-4">
              Testimonials
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
              What Our Patients
              <span className="text-gradient"> Are Saying</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Don't just take our word for it. Hear from the thousands of patients 
              whose smiles we've transformed.
            </p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card
                key={testimonial.id}
                className={`border-0 shadow-lg hover:shadow-xl card-hover bg-gradient-to-br from-white to-slate-50 animate-fade-in-up`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8">
                  {/* Quote icon */}
                  <Quote className="w-10 h-10 text-teal-200 mb-4" />
                  
                  {/* Rating */}
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 fill-amber-400 text-amber-400"
                      />
                    ))}
                  </div>

                  {/* Text */}
                  <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                    "{testimonial.text}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-teal-100"
                    />
                    <div>
                      <p className="font-bold text-slate-800">{testimonial.name}</p>
                      <p className="text-sm text-teal-600 font-medium">
                        {testimonial.treatment}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Before/After Section */}
        <div>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 bg-amber-50 text-amber-700 font-semibold text-sm rounded-full mb-4">
              Smile Transformations
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
              See The
              <span className="text-gradient"> Real Results</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Witness the incredible transformations we've achieved for our patients.
            </p>
          </div>

          {/* Before/After Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {beforeAfter.map((item, index) => (
              <div
                key={item.id}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl card-hover animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Before Image */}
                <div className="relative">
                  <div className="grid grid-cols-2">
                    <div className="relative">
                      <img
                        src={item.before}
                        alt="Before treatment"
                        className="w-full h-48 object-cover"
                      />
                      <span className="absolute bottom-2 left-2 bg-slate-800/80 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        Before
                      </span>
                    </div>
                    <div className="relative">
                      <img
                        src={item.after}
                        alt="After treatment"
                        className="w-full h-48 object-cover"
                      />
                      <span className="absolute bottom-2 right-2 bg-teal-600/90 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        After
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Treatment info */}
                <div className="bg-white p-4">
                  <p className="font-semibold text-slate-800 text-center">
                    {item.treatment}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <p className="text-center text-sm text-slate-400 mt-8">
            *Individual results may vary. Photos shown are of actual patients who 
            have given permission for their use.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Results;
